/**
 * OVERRIDE View.jsx
 * REASON: 1. The browser back button should navigate to the previous page, not the previous accordion.
 *         3. Keep the page identity on the panel so blocks inside it can still
 *            derive the page path (see #1700).
 * FILE: https://github.com/kitconcept/volto-accordion-block/blob/f3810eecc203c141f14920b057bcae63f2652f35/packages/volto-accordion-block/src/components/View.jsx
 * FILE VERSION: "tag": "scroll-open-accordion-af9u"
 * TICKET: https://gitlab.fhnw.ch/webteam/fhnw-webauftritt-2025/-/issues/870
 * DATE: 2025-09-02
 * DEVELOPER: @iFlameing
 * UPDATED: 2026-03-17 - Performance optimization (lazy loading)
 * UPDATED: 2026-08-06 - Pass the page @id into the panel (#1700)
 */

import React from 'react';
import {
  getPanels,
  accordionBlockHasValue,
} from '@kitconcept/volto-accordion-block/components/util.js';
import { withBlockExtensions } from '@plone/volto/helpers';
import { useLocation } from 'react-router-dom';
import cx from 'classnames';
import config from '@plone/volto/registry';
import { RenderBlocks } from '@plone/volto/components';

import { Accordeon } from '@designsystem/stories/04-organisms/Accordeon';

const CLOSED_PANEL_BLOCKS = [
  'slate',
  'slateTable',
  'heading',
  'buttons',
  'teaser',
];

const withoutImage = (block) =>
  block?.['@type'] === 'teaser' ? { ...block, showTeaserImage: false } : block;

const closedPanel = (panel) => {
  const items = (panel?.blocks_layout?.items || []).filter((blockId) =>
    CLOSED_PANEL_BLOCKS.includes(panel?.blocks?.[blockId]?.['@type']),
  );
  return {
    ...panel,
    blocks: Object.fromEntries(
      Object.entries(panel?.blocks || {}).map(([blockId, block]) => [
        blockId,
        withoutImage(block),
      ]),
    ),
    blocks_layout: { ...panel?.blocks_layout, items },
  };
};

const useQuery = (location) => {
  const { search } = location;
  return React.useMemo(() => new URLSearchParams(search), [search]);
};

const View = (props) => {
  const { data, className } = props;
  const location = useLocation();
  const panels = getPanels(data.data);
  const metadata = props.metadata || props.properties;
  const non_exclusive = config.blocks?.blocksConfig?.accordion?.non_exclusive;

  const query = useQuery(location);
  const [hashId, setHashId] = React.useState('');
  React.useEffect(() => {
    setHashId(location.hash.slice(1));
  }, [location.hash]);
  const activePanels = [
    ...(query.get('activeAccordion')?.split(',') || []),
    ...(hashId ? [hashId] : []),
  ];

  // A panel carries the blocks but is not a content object of its own. Blocks
  // that derive the page path from `content['@id']` — the IRF block builds its
  // widget id from it — would otherwise lose the page they sit on. #1700
  const pageId = props.content?.['@id'] || metadata?.['@id'];

  const convertedPanels = [];
  panels.forEach(([id, panel]) => {
    if (!accordionBlockHasValue(panel)) {
      return;
    }
    convertedPanels.push({
      id: id,
      open: activePanels && activePanels.includes(id),
      title: panel?.title,
      content: (open) => (
        <RenderBlocks
          {...props}
          location={location}
          metadata={metadata}
          content={{ ...(open ? panel : closedPanel(panel)), '@id': pageId }}
        />
      ),
    });
  });

  return (
    <div className={cx('block accordionBlock', className)}>
      {data.headline && <h2 className="headline">{data.headline}</h2>}
      <Accordeon
        items={convertedPanels}
        onlyone={!non_exclusive}
        key={hashId}
      />
    </div>
  );
};

export default withBlockExtensions(View);
